import Vue from "vue";
import Router from "vue-router";
import apiClient from './utils/apiClient.js';
import { setStatusData, clearStatusData } from './utils/statusCache.js';

Vue.use(Router);

var router = new Router({
  mode: "history",
  base: "/viva/",
  routes: [
    {
      path: "/",
      name: "landing",
      component: () => import("./views/Landing.vue"),
      meta: {
        title: "Viva"
      }
    },
    {
      path: "/register",
      name: "register",
      component: () => import("./views/Register.vue"),
      meta: {
        title: "Viva - Register"
      }
    },
    {
      path: "/login",
      name: "login",
      component: () => import("./views/Login.vue"),
      meta: {
        title: "Viva - Login"
      }
    },
    {
      path: "/upload",
      name: "upload",
      component: () => import("./views/Upload.vue"),
      meta: {
        title: "Viva - Upload Project"
      }
    },
    {
      path: "/payment",
      name: "payment",
      component: () => import("./views/Payment.vue"),
      meta: {
        title: "Viva - Payment"
      }
    },
    {
      path: "/practice",
      name: "practice",
      component: () => import("./views/Practice.vue"),
      meta: {
        title: "Viva - Practice"
      }
    },
    {
      path: "/record",
      name: "record",
      component: () => import("./views/Record.vue"),
      meta: {
        title: "Viva - Recording"
      }
    },
    {
      path: "/send",
      name: "send",
      component: () => import("./views/Send.vue"),
      meta: {
        title: "Viva - Send to Schools"
      }
    },
    {
      path: "/success",
      name: "success",
      component: () => import("./views/Success.vue"),
      meta: {
        title: "Viva - Success"
      }
    },
    {
      path: "/terms-and-conditions",
      name: "terms-and-conditions",
      component: () => import("./views/TermsAndConditions.vue"),
      meta: {
        title: "Viva - Terms and Conditions"
      }
    },
    {
      path: "/privacy-policy",
      name: "privacy-policy",
      component: () => import("./views/PrivacyPolicy.vue"),
      meta: {
        title: "Viva - Privacy Policy"
      }
    },
    {
      path: "/review-student",
      name: "review-student",
      component: () => import("./views/Review.vue"),
      meta: {
        title: "Viva - Review Student"
      }
    },
    {
      path: "/qa",
      name: "qa",
      component: () => import("./views/QA.vue"),
      meta: {
        title: "Viva - QA Review"
      }
    },
    {
      path: "/faqs",
      name: "faqs",
      component: () => import("./views/FAQs.vue"),
      meta: {
        title: "Viva - FAQs"
      }
    },
    {
      path: "/student/portal",
      name: "admissions-portal",
      component: () => import("./views/AdmissionsPortal.vue"),
      meta: {
        title: "Viva - My VIVAs"
      }
    },
    {
      path: "/student/dashboard",
      name: "student-dashboard",
      component: () => import("./views/StudentDashboard.vue"),
      meta: {
        title: "Viva - My Courses"
      }
    },
    {
      path: "/student/course/:id/assignments",
      name: "student-course-assignments",
      component: () => import("./views/StudentCourseAssignments.vue"),
      meta: {
        title: "Viva - Course Assignments"
      }
    },
    {
      path: "/student/assignment/:id",
      name: "student-assignment-detail",
      component: () => import("./views/StudentAssignmentDetail.vue"),
      meta: {
        title: "Viva - Assignment Details"
      }
    },
    {
      path: "/instructor-register",
      name: "instructor-register",
      component: () => import("./views/InstructorRegister.vue"),
      meta: {
        title: "Viva - Instructor Registration"
      }
    },
    {
      path: "/admin/dashboard",
      name: "admin-dashboard",
      component: () => import("./views/AdminDashboard.vue"),
      meta: {
        title: "Viva - Admin Dashboard"
      }
    },
    {
      path: "/approval",
      name: "approval",
      component: () => import("./views/Approval.vue"),
      meta: {
        title: "Viva - Account Approval"
      }
    },
    {
      path: "/instructor/dashboard",
      name: "instructor-dashboard",
      component: () => import("./views/InstructorDashboard.vue"),
      meta: {
        title: "Viva - My Courses"
      }
    },
    {
      path: "/instructor/course/:id/edit",
      name: "course-edit",
      component: () => import("./views/CourseForm.vue"),
      meta: {
        title: "Viva - Edit Course"
      }
    },
    {
      path: "/instructor/course/:id/assignments",
      name: "course-assignments",
      component: () => import("./views/AssignmentList.vue"),
      meta: {
        title: "Viva - Assignments"
      }
    },
    {
      path: "/instructor/assignment/:id/edit",
      name: "assignment-edit",
      component: () => import("./views/AssignmentForm.vue"),
      meta: {
        title: "Viva - Edit Assignment"
      }
    },
    {
      path: "/instructor/assignment/:id/submissions",
      name: "assignment-submissions",
      component: () => import("./views/StudentSubmissions.vue"),
      meta: {
        title: "Viva - Student Submissions"
      }
    },
    {
      path: "/instructor/student/:student_id/progress",
      name: "student-progress",
      component: () => import("./views/StudentProgress.vue"),
      meta: {
        title: "Viva - Student Progress"
      }
    }
  ]
});

// Authentication and workflow navigation guard
router.beforeEach(async (to, from, next) => {
  // Handle vparam in URL
  if (to.query.vparam) {
    console.log('vparam detected in URL');
    // New vparam in URL - update sessionStorage
    const newVparam = to.query.vparam;
    const oldVparam = sessionStorage.getItem('vparam');

    // If it's a different vparam, clear the old one
    if (oldVparam && oldVparam !== newVparam) {
      console.log('Switching to new vparam, clearing old session data');
      sessionStorage.removeItem('vparam');
      sessionStorage.removeItem('upload_token');
      clearStatusData();
    }

    sessionStorage.setItem('vparam', newVparam);
  } else if (to.name === 'login' || to.name === 'register' || to.name === 'instructor-register') {
    // User is going to login/register page - clear all authentication data
    console.log('Clearing vparam and tokens on login/register page');
    sessionStorage.removeItem('vparam');
    sessionStorage.removeItem('upload_token');
    sessionStorage.removeItem('apitoken');
    clearStatusData();
    // Remove auth header - was causing bugs when switching accounts
    delete apiClient.defaults.headers["Authorization"];
    delete apiClient.defaults.headers.common["Authorization"];
  }

  const token = sessionStorage.apitoken;
  const vparam = sessionStorage.getItem('vparam');
  const publicRoutes = [
    'landing',
    'register',
    'instructor-register',
    'login',
    'terms-and-conditions',
    'privacy-policy',
    'review-student',
    'faqs'
  ];

  // Student portal routes support both token authentication and vparam
  const studentPortalRoutes = ['student-dashboard', 'student-course-assignments', 'student-assignment-detail'];
  const isStudentPortalRoute = studentPortalRoutes.includes(to.name);

  const authRequired = !publicRoutes.includes(to.name);

  if (!authRequired) {
    return next();
  }

  // Require authentication: either token (authenticated users) OR vparam (coursework students)
  // For student portal routes: if no authentication, redirect to login with redirect URL
  else if (authRequired && !token && !vparam) {
    const redirectPath = to.fullPath !== '/login' ? to.fullPath : '/upload';
    return next({
      name: 'login',
      query: { redirect: redirectPath }
    });
  }
  
  // If authenticated, check for initial landing redirect
  else {
    // Only redirect on initial navigation (fresh login/page refresh)
    const isInitialNavigation = !from.name || 
                               from.path === '/login' || 
                               from.path === '/register' ||
                               from.path === '/' ||
                               from.name === 'register' ||
                               from.name === 'login';
    
    // Check if trying to access instructor routes
    const instructorRoutes = ['instructor-dashboard', 'course-edit', 'course-assignments', 'assignment-edit', 'assignment-submissions', 'student-progress'];
    const isInstructorRoute = instructorRoutes.includes(to.name);
    const directRoutes = ['admissions-portal', 'admin-dashboard', 'approval'];
    const isDirectRoute = directRoutes.includes(to.name) || isInstructorRoute;

    // isStudentPortalRoute already defined above

    try {
      // Include vparam in request if present
      const params = vparam ? { vparam } : {};
      const response = await apiClient.get('/api/viva/fetch_status/', { params });
      if (response.data.error) {
        console.log('Error in status response:', response.data.error);
        if (response.data.status === 401) {
          console.warn('Authentication failed - redirecting to login');
          // Clear session storage
          sessionStorage.removeItem('apitoken');
          sessionStorage.removeItem('vparam');
          sessionStorage.removeItem('upload_token');
          // Redirect to login with the current path as redirect target
          return next({
            name: 'login',
            query: { redirect: to.fullPath }
          });
        } else if (response.data.status === 404) {
          // This means they are logged in but trying to access someone else's viva
          alert("I'm sorry, we couldn't find your viva. Please try logging in again. If the problem persists, contact support at viva@initialview.com");
        }
      }
      setStatusData(response.data);
      console.log('User status:', response.data.student_status);
      console.log('Is VIVA instructor:', response.data.is_viva_instructor);

      // Store upload token for file uploads (coursework students with vparam)
      if (response.data.upload_token) {
        sessionStorage.upload_token = response.data.upload_token;
      }

      // Block access to instructor routes if not an instructor
      if (isInstructorRoute && !response.data.is_viva_instructor) {
        console.warn('User attempted to access instructor route without permission');
        return next('/upload');
      }

      // Block access to admin routes if not an admin
      const adminRoutes = ['admin-dashboard'];
      const isAdminRoute = adminRoutes.includes(to.name);
      if (isAdminRoute && !response.data.is_viva_admin) {
        console.warn('User attempted to access admin route without permission');
        return next('/upload');
      }

      // Block access to approval routes if not QA
      const qaOnlyRoutes = ['approval', 'qa'];
      const isQAOnlyRoute = qaOnlyRoutes.includes(to.name);
      if (isQAOnlyRoute && response.data.student_status !== 'viva_qa') {
        console.warn('User attempted to access QA-only route without permission');
        return next('/upload');
      }

      // Only redirect on initial navigation to non-instructor and non-student-portal routes
      if (isInitialNavigation && !isDirectRoute && !isStudentPortalRoute) {
        const statusRoutes = {
          "ready_to_upload": "/upload",
          "ready_to_pay": "/payment",
          "ready_to_record": "/practice",
          "ready_to_review": "/send",
          "completed": "/success",  // Coursework completion
          "portal": "/student/portal",  // Admissions portal for users with existing vivas
          "viva_qa": "/qa",
          "viva_admin": "/admin/dashboard"
        };

        let targetRoute = statusRoutes[response.data.student_status] || "/upload";

        // If user is an instructor with no pending admissions vivas, redirect to instructor dashboard
        if (response.data.is_viva_instructor && targetRoute === "/upload") {
          targetRoute = "/instructor/dashboard";
        }
        // If user is an assignment student and would still go to upload, redirect to student dashboard instead
        else if (response.data.is_assignment_student && targetRoute === "/upload") {
          targetRoute = "/student/dashboard";
        }

        // Only redirect if they're not already going to the right place
        if (to.path !== targetRoute) {
          return next(targetRoute);
        }
      }
    } catch (error) {
      console.error('Error fetching user status:', error);

      // Clear session and redirect to login - status is unknown
      sessionStorage.removeItem('apitoken');
      sessionStorage.removeItem('upload_token');
      // Keep vparam - it may be needed for coursework students to re-authenticate

      return next({
        name: 'login',
        query: {
          redirect: to.fullPath,
          error: 'session_error'
        }
      });
    }
  }
  
  next();
});

export default router;